import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Bot, MessageSquare, HeartPulse, GitBranch, Code2, Cog,
  ArrowRight, ShoppingCart, Globe, Users, Layers, Database,
  Brain, CheckCircle, Copy, Shield, DollarSign, Megaphone,
  Package, Truck, SmilePlus, Search, Share2, ChevronRight,
  Lock, Eye, Key, FileCheck, Server
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsWelcome() {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const capabilities = [
    { icon: Bot, title: '80+ AI Agents', desc: 'Department-organized: 1 AI CEO, 13 directors, 60+ specialists across supply chain, marketplaces, fulfillment, finance, marketing, and compliance. Every agent observes, decides, acts, and learns.', color: '#3EF1C7' },
    { icon: MessageSquare, title: 'Messaging-First (OpenClaw)', desc: 'Manage your store from Telegram, WhatsApp, iMessage, Slack, and Email with 30+ commands.', color: '#7A5CFF' },
    { icon: HeartPulse, title: 'Commerce Health Score', desc: 'A real-time composite score (0–1000) across pricing, inventory, fulfillment, CX, and SEO dimensions.', color: '#FFD166' },
    { icon: GitBranch, title: 'Decision Graph', desc: 'Transparent reasoning traces showing why each agent made each decision, with full auditability.', color: '#FF6B8A' },
    { icon: Code2, title: 'API Platform', desc: 'RESTful APIs for products, deals, agents, inventory, orders, and analytics. Build on top of Commerce Index.', color: '#38BDF8' },
    { icon: Cog, title: '4 Commerce Engines', desc: 'Funding Engine, Distribution Engine, Feedback Engine, and Product Engine — the core computation layer.', color: '#FB923C' },
    { icon: Shield, title: '5-Layer Security', desc: 'API key auth, OAuth 2.0, role-based access, agent guardrails with trust ladders, and end-to-end encryption.', color: '#A78BFA' },
  ];

  const agents = [
    { icon: DollarSign, name: 'Pricing Agent', desc: 'Monitors competitor prices and recommends optimal pricing strategies.', color: '#3EF1C7' },
    { icon: Megaphone, name: 'Deal Agent', desc: 'Creates and manages promotions, publishes deals to thedeals.ai.', color: '#7A5CFF' },
    { icon: Package, name: 'Inventory Agent', desc: 'Predicts stockouts and calculates reorder points automatically.', color: '#FFD166' },
    { icon: Truck, name: 'Fulfillment Agent', desc: 'Tracks shipments, detects exceptions, and manages delivery promises.', color: '#FF6B8A' },
    { icon: SmilePlus, name: 'CX Agent', desc: 'Monitors customer sentiment, handles escalations and support routing.', color: '#38BDF8' },
    { icon: Search, name: 'SEO Agent', desc: 'Optimizes product listings for search engine visibility and ranking.', color: '#FB923C' },
    { icon: Share2, name: 'Distribution Agent', desc: 'Manages multi-channel distribution across marketplaces and platforms.', color: '#A78BFA' },
  ];

  const securityLayers = [
    { icon: Key, title: 'API Key Authentication', desc: 'Every request requires a valid API key tied to your account.' },
    { icon: Lock, title: 'OAuth 2.0', desc: 'Secure authorization flows for Shopify and third-party integrations.' },
    { icon: Users, title: 'Role-Based Access', desc: 'Granular permissions for team members — owner, admin, editor, viewer.' },
    { icon: Shield, title: 'Agent Guardrails', desc: 'Trust ladders control what agents can do autonomously vs. what needs approval.' },
    { icon: FileCheck, title: 'End-to-End Encryption', desc: 'All data in transit and at rest is encrypted with AES-256.' },
  ];

  const quickLinks = [
    { title: 'Shopify Setup Guide', desc: 'Install from the App Store and get running in minutes.', link: '/docs/shopify-setup', icon: ShoppingCart, color: '#3EF1C7' },
    { title: 'Non-Shopify Guide', desc: 'Import via CSV or API for any e-commerce platform.', link: '/docs/manual-setup', icon: Server, color: '#7A5CFF' },
    { title: 'OpenClaw Setup', desc: 'Connect messaging and manage your store from your phone.', link: '/docs/openclaw', icon: MessageSquare, color: '#FFD166' },
    { title: 'Agent Handbook', desc: 'Deep dive into the AI CEO + 13 directors + 60+ specialists.', link: '/docs/agents', icon: Bot, color: '#FF6B8A' },
  ];

  const architectureBlock = `┌─────────────────────────────────────────────────┐
│                   Frontend                       │
│           React + Tailwind + Vite                │
├─────────────────────────────────────────────────┤
│               Messaging Layer                    │
│         OpenClaw (TypeScript/Node.js)            │
│   Telegram · WhatsApp · iMessage · Slack · Email │
├─────────────────────────────────────────────────┤
│                 Backend API                       │
│            Python / FastAPI                       │
│   Agents · Engines · Webhooks · Scheduling       │
├─────────────────────────────────────────────────┤
│                  Database                         │
│                  MongoDB                          │
├─────────────────────────────────────────────────┤
│                 LLM Layer                         │
│            Ollama (local) + Claude (cloud)        │
└─────────────────────────────────────────────────┘`;

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Platform Overview
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Welcome to Commerce Index
        </h1>
        <p className="text-lg text-[#94A3B8] mb-6">
          The agentic commerce operating system. One AI CEO + 13 directors + 60+ specialists that observe, decide, act, and learn —
          so you can run your store smarter, not harder.
        </p>
        <div className="flex gap-4">
          <Link to="/docs/getting-started">
            <Button className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90">
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/docs/agents">
            <Button variant="outline" className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B]">
              Meet the Agents
            </Button>
          </Link>
        </div>
      </div>

      {/* What is Commerce Index */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What is Commerce Index?
        </h2>
        <div className="bg-[#0D1628] border border-[#22304A] rounded-lg p-6 mb-6">
          <p className="text-[#94A3B8] mb-4">
            Commerce Index is an <span className="text-[#3EF1C7] font-semibold">agentic commerce operating system</span> —
            a platform where AI agents autonomously manage the complex, repetitive decisions that come with running an
            e-commerce business.
          </p>
          <p className="text-[#94A3B8] mb-4">
            Instead of manually adjusting prices, creating promotions, monitoring inventory, tracking shipments,
            analyzing customer sentiment, optimizing SEO, and managing multi-channel distribution — you configure
            guardrails and let <span className="text-white font-medium">your AI CEO + 13 directors + 60+ specialists</span> handle it for you.
          </p>
          <p className="text-[#94A3B8]">
            Each agent operates within a trust ladder — starting with observation-only and gradually earning more
            autonomy as you build confidence in its decisions. You stay in control. The agents do the work.
          </p>
        </div>
      </section>

      {/* Three Platforms */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Three Platforms
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Layers className="w-8 h-8 text-[#3EF1C7] mb-2" />
              <CardTitle className="text-white text-lg">commerceindex.com</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                The merchant platform. Dashboard, agent management, product catalog, deal creation, analytics,
                and the full command center for your commerce operations.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Brain className="w-8 h-8 text-[#7A5CFF] mb-2" />
              <CardTitle className="text-white text-lg">commerceindex.ai</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                The agent marketplace. Browse, configure, and deploy AI agents. Community-built agent templates,
                pre-built workflows, and agent performance benchmarks.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <ShoppingCart className="w-8 h-8 text-[#FFD166] mb-2" />
              <CardTitle className="text-white text-lg">thedeals.ai</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                The consumer platform. AI-curated deals, personalized recommendations, price tracking,
                and a deal discovery experience powered by the Commerce Index engine.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Core Capabilities
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return (
              <Card key={i} className="bg-[#0D1628] border-[#22304A]">
                <CardHeader className="pb-2">
                  <Icon className="w-8 h-8 mb-2" style={{ color: cap.color }} />
                  <CardTitle className="text-white text-lg">{cap.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#94A3B8]">{cap.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Featured AI Agents */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Meet a Few of the Agents
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {agents.map((agent, i) => {
            const Icon = agent.icon;
            return (
              <div key={i} className="flex items-start gap-3 p-4 bg-[#0D1628] border border-[#22304A] rounded-lg">
                <Icon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: agent.color }} />
                <div>
                  <h3 className="text-white font-medium">{agent.name}</h3>
                  <p className="text-sm text-[#94A3B8]">{agent.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4">
          <Link to="/docs/agents" className="text-[#3EF1C7] hover:underline text-sm flex items-center gap-1">
            Deep dive into all agents <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </section>

      {/* 5-Layer Security */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          5-Layer Security
        </h2>
        <div className="space-y-3">
          {securityLayers.map((layer, i) => {
            const Icon = layer.icon;
            return (
              <div key={i} className="flex items-start gap-3 p-4 bg-[#0D1628] border border-[#22304A] rounded-lg">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#A78BFA]/10 flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#A78BFA]" />
                </div>
                <div>
                  <h3 className="text-white font-medium">{layer.title}</h3>
                  <p className="text-sm text-[#94A3B8]">{layer.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Architecture Overview */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Architecture Overview
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Commerce Index is a full-stack platform built with modern, scalable technologies:
        </p>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(architectureBlock)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{architectureBlock}</code>
          </pre>
        </div>
      </section>

      {/* Quick Links */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Quick Links
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {quickLinks.map((item, i) => {
            const Icon = item.icon;
            return (
              <Link key={i} to={item.link}>
                <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer h-full">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <Icon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: item.color }} />
                      <div>
                        <h3 className="text-white font-medium mb-1">{item.title}</h3>
                        <p className="text-sm text-[#94A3B8]">{item.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* What's Next */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What's Next?
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <ul className="space-y-3">
              {[
                { text: 'Choose your setup path', link: '/docs/getting-started' },
                { text: 'Explore all 80+ AI agents', link: '/docs/agents' },
                { text: 'Follow the Onboarding Checklist', link: '/docs/onboarding-checklist' },
                { text: 'Set up messaging with OpenClaw', link: '/docs/openclaw' },
              ].map((item, i) => (
                <li key={i}>
                  <Link to={item.link} className="flex items-center gap-2 text-[#3EF1C7] hover:underline">
                    <ChevronRight className="w-4 h-4" />
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
